<template>
  <nav class="main-nav no-scrollbar">
    <CompanyLogoContainer />
    <section
      class="is-flex is-flex-direction-column is-justify-content-space-between menu"
      :class="{ 'mt-5': isPoweredBy }"
      :data-e2e="NavCypressAttribute.views.MENU"
    >
      <ul v-if="canShowHeader" class="menu-list" :data-e2e="NavCypressAttribute.views.HEADER">
        <template v-for="item in headerNavItems" :key="item.key">
          <component v-if="item.customComponent" :is="item.customComponent" :item="item" />
          <NavItemComponent v-else :item="item" />
        </template>
      </ul>
      <ul class="menu-list is-full-height">
        <MainNavLoader v-if="isLoading" />
        <template v-else>
          <p
            v-if="primaryNavItem && hasMultipleNavGroups && primaryNavItem.group"
            class="has-text-weight-semibold has-text-grey is-uppercase is-size-6 m-l-15 m-b-10 is-hidden-mobile"
            :data-e2e="NavCypressAttribute.texts.NAV_GROUP_TITLE"
          >
            {{ primaryNavItem.group.title }}
          </p>
          <NavItemComponent
            v-if="primaryNavItem"
            :key="primaryNavItem.key"
            :item="primaryNavItem"
          />
          <div
            :data-guide="DataGuide.PLAN_OVERVIEW_NAV_PANEL"
            :data-e2e="NavCypressAttribute.views.NAV_GROUP_PANEL"
          >
            <template v-for="(group, index) in groupedNavItemsExcludingLast" :key="group.key">
              <p
                v-if="hasMultipleNavGroups && group.key !== primaryNavItemGroupKey"
                class="has-text-weight-semibold has-text-grey is-uppercase is-size-6 m-l-15 m-b-10 is-hidden-mobile"
                :data-e2e="NavCypressAttribute.texts.NAV_GROUP_TITLE"
              >
                {{ group.title }}
              </p>
              <NavItemComponent v-for="item in group.items" :key="item.key" :item="item" />
              <hr
                v-if="hasMultipleNavGroups && index < groupedNavItemsExcludingLast.length - 1"
                class="group-separator m-b-15 m-r-10 is-hidden-mobile"
                :data-e2e="NavCypressAttribute.views.NAV_GROUP_SEPARATOR"
              />
            </template>
          </div>
          <template v-for="group in lastNavGroup" :key="group.key">
            <p
              v-if="hasMultipleNavGroups && group.key !== primaryNavItemGroupKey"
              class="has-text-weight-semibold has-text-grey is-uppercase is-size-6 m-l-15 m-b-10 is-hidden-mobile"
              :data-e2e="NavCypressAttribute.texts.NAV_GROUP_TITLE"
            >
              {{ group.title }}
            </p>
            <NavItemComponent v-for="item in group.items" :key="item.key" :item="item" />
          </template>
        </template>
        <li
          v-if="isViewingEstate"
          class="exit-estate is-clickable has-text-weight-semibold"
          :data-e2e="NavCypressAttribute.buttons.EXIT_ESTATE"
          @click="clearCurrentEstate"
        >
          <img v-if="!isExitingEstate" class="m-r-5 exit-icon" src="@/assets/exit-estate.svg" />
          <i v-else class="cls cls-synchronize-arrow spin"></i>
          {{ $t('core.exit-estate') }}
        </li>
      </ul>
      <ul v-if="canShowFooter" class="menu-list" :data-e2e="NavCypressAttribute.views.FOOTER">
        <template v-for="item in footerNavItems" :key="item.key">
          <component v-if="item.customComponent" :is="item.customComponent" :item="item" />
          <NavItemComponent v-else :item="item" />
        </template>
      </ul>
      <UserMenu class="is-hidden-mobile" />
    </section>
  </nav>
</template>

<script>
import { LocalStorageKey } from '/@/modules/core/app/constants/app.constants';
import { NavService } from '/@/modules/core/nav/services/NavService';
import NavItemComponent from '/@/modules/core/nav/components/NavItem.vue';
import MainNavLoader from '/@/modules/core/nav/components/MainNavLoader.vue';
import { NavEvents } from '/@/modules/core/nav/constants/nav.constants';
import UserMenu from '/@/modules/core/nav/components/UserMenu.vue';
import CurrentEstateService from '/@/modules/estate/services/CurrentEstateService';
import AuthService from '/@/modules/auth/services/AuthService';
import { HasCurrentEstateMixin } from '/@/modules/core/app/mixins/HasCurrentEstateMixin';
import { HasAuthUserMixin } from '/@/modules/core/app/mixins/HasAuthUserMixin';
import InboxNavItem from '/@/modules/inbox/components/InboxNavItem.vue';
import NotificationsNavItem from '/@/modules/notification/components/stateful/NotificationsNavItem.vue';
import { NavCypressAttribute } from '/@/modules/core/nav/constants/NavCypressAttribute';
import Logger from '/@/modules/core/app/helpers/Logger';
import CompanyLogoContainer from '/@/modules/core/nav/components/CompanyLogoContainer.vue';
import { commits } from '/@/modules/core/app/helpers/store';
import HasCompanyMixin from '/@/modules/company/mixins/HasCompanyMixin';
import GiveFeedbackNavItem from '@/modules/admin/components/resource/GiveFeedbackNavItem.vue';
import FinancialAdvisorAdvisorProUpgradeNavItem from '@/modules/financialadvisor/components/nav/FinancialAdvisorAdvisorProUpgradeNavItem.vue';
import TeamDropdownNavItem from '@/modules/team/components/TeamDropdownNavItem.vue';
import NavOrderingService from '@/modules/core/nav/services/NavOrderingService';
import { NavKey } from '@/modules/core/nav/constants/NavKey';
import { DataGuide } from '@/modules/core/tour/enums/DataGuide';
import { TeamEvent } from '@/modules/team/enums/TeamEvent';
import { HasContainerMixin } from '@/modules/core/app/mixins/HasContainerMixin';
import { state } from '/@/modules/core/app/helpers/store';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'MainNav',
  mixins: [HasAuthUserMixin, HasCurrentEstateMixin, HasCompanyMixin, HasContainerMixin],
  components: {
    CompanyLogoContainer,
    InboxNavItem,
    TeamDropdownNavItem,
    GiveFeedbackNavItem,
    NotificationsNavItem,
    MainNavLoader,
    NavItemComponent,
    UserMenu,
    FinancialAdvisorAdvisorProUpgradeNavItem
  },
  static: {
    NavCypressAttribute,
    DataGuide
  },
  events() {
    return {
      [NavEvents.RELOAD]: this.loadNav,
      [TeamEvent.ON_TEAM_CHANGED]: this.loadNav,
      [TeamEvent.ON_TEAM_ADDED]: this.loadNav,
      [TeamEvent.ON_TEAM_UPDATED]: this.loadNav,
      [TeamEvent.ON_TEAM_DELETED]: this.loadNav
    };
  },
  data() {
    return {
      /** @var {boolean} */
      isLoading: true,
      navService: this.$resolve(NavService)
    };
  },
  computed: {
    navItems() {
      return NavOrderingService.order(state.nav.navItems, this.authUser);
    },
    primaryNavItem() {
      return this.navItems.find((item) => item.key === NavKey.NAV_DASHBOARD);
    },
    panelNavItems() {
      return this.navItems.filter((item) => item.key !== NavKey.NAV_DASHBOARD);
    },
    primaryNavItemGroupKey() {
      return this.primaryNavItem?.group?.key;
    },
    groupedNavItems() {
      return this.panelNavItems.reduce((groups, item) => {
        const group = groups.find((group) => group.key === item.group?.key);

        if (group) {
          group.items.push(item);
          return groups;
        }

        groups.push({ key: item.group?.key, title: item.group?.title, items: [item] });

        return groups;
      }, []);
    },
    hasMultipleNavGroups() {
      return this.groupedNavItems.length > 1;
    },
    groupedNavItemsExcludingLast() {
      return this.groupedNavItems.slice(0, -1);
    },
    lastNavGroup() {
      return this.groupedNavItems.slice(-1);
    },
    footerNavItems() {
      return state.nav.footerNavItems;
    },
    headerNavItems() {
      return state.nav.headerNavItems;
    },
    isExitingEstate() {
      return state.ui.isExitingEstate;
    },
    isViewingEstate() {
      return this.authUser?.isViewingEstate && !this.authUser?.isImpersonated && !this.isLoading;
    },
    canShowFooter() {
      return !this.isLoading && this.footerNavItems?.length;
    },
    canShowHeader() {
      return !this.isLoading && this.headerNavItems?.length;
    }
  },
  methods: {
    async loadNav() {
      this.isLoading = true;
      try {
        await this.navService.getNavItems();
      } finally {
        this.isLoading = false;
      }
    },
    async redirectUser() {
      if (
        this.authUser.isEstateProfessional ||
        this.authUser.isJuniorEstateProfessional ||
        this.authUser.isSuperAdmin
      ) {
        const redirectUrlFullPath = localStorage.getItem(LocalStorageKey.REDIRECT_URL_FULL_PATH);
        if (redirectUrlFullPath) {
          await this.$router.push(redirectUrlFullPath);
          localStorage.removeItem(LocalStorageKey.REDIRECT_URL_FULL_PATH);
        }
        return;
      }

      await this.$router.push({ name: this.authUser.homePage });
    },
    async clearCurrentEstate() {
      if (this.isExitingEstate) {
        return;
      }

      commits.ui.setIsExitingEstate(true);
      try {
        await CurrentEstateService.clearCurrentEstate();
        await AuthService.refreshAuthUser();
        await this.redirectUser();
        await this.loadNav();
      } catch (e) {
        Logger.log(e, Logger.LEVEL_ERROR);
      } finally {
        commits.ui.setIsExitingEstate(false);
      }
    }
  },
  created() {
    this.loadNav();
  }
};
</script>

<style scoped>
nav.main-nav {
  background-color: var(--ce-white);
  box-shadow: 3px 0 8px rgba(0, 0, 0, 0.06);

  .menu {
    width: 100%;
    padding-bottom: var(--ce-15px);
    font-size: var(--ce-20px);
    z-index: var(--ce-z-index-1);
    border-radius: 0 15px 0 0;
    position: absolute;
    right: 0;
    left: 0;
    bottom: 0;
    top: var(--ce-90px);
  }

  hr.group-separator {
    height: var(--ce-1px);
    background-color: var(--ce-border);
  }

  p.menu-label {
    margin-left: var(--ce-22px);
    font-weight: var(--ce-weight-bold);
    font-size: var(--ce-12-5px);
    color: var(--ce-text-strong);
    margin-bottom: var(--ce-size-normal);
  }

  .menu-list {
    padding-top: var(--ce-4px);
  }

  .exit-estate {
    color: var(--ce-primary);
    font-size: var(--ce-16px);
    margin-left: var(--ce-25px);
    margin-top: var(--ce-10px);

    .exit-icon {
      transition: all 0.3s ease;
      position: relative;
      top: var(--ce-5px);

      svg {
        fill: var(--ce-primary) !important;
      }
    }

    &:hover {
      .exit-icon {
        margin-left: var(--ce--10px);
      }
    }
  }
}
</style>
